import {
  type ServiceActivationContext,
  type ServiceRegistration,
} from '@realitycollective/service-framework';
import { Room } from 'livekit-client';
import { ILiveKitRoomService } from './ILiveKitRoomService';
import { LiveKitRoomService, type LiveKitRoomConfig, type RoomLike } from './LiveKitRoomService';
import { IDetectionService } from '../detection/IDetectionService';
import { DetectionService, type DetectionConfig } from '../detection/DetectionService';
import type { IWebRTCService } from '../webrtc/IWebRTCService';
import { IAssistantRosterService } from './IAssistantRosterService';
import { AssistantRosterService, type AssistantRosterConfig } from './AssistantRosterService';
import { IAssistSessionService } from './IAssistSessionService';
import { AssistSessionService, type AssistSessionConfig } from './AssistSessionService';
import { IAssistStrokeService } from './IAssistStrokeService';
import { AssistStrokeService, type AssistStrokeConfig } from './AssistStrokeService';
import { ITrainingStateService } from '../training/ITrainingStateService';
import { RemoteTrainingStateService, type RemoteTrainingConfig } from '../training/RemoteTrainingStateService';

/**
 * The one place the real `livekit-client` Room is constructed. Room-level
 * options live here rather than on `connect()` because that is where the JS
 * SDK takes them: Dynacast pauses publishes nobody watches; adaptiveStream is
 * OFF - subscriptions are deliberate wearer choices, not view-size driven, and
 * an invisible panel must not silently pause its feed.
 */
export function createLiveKitRoomFactory(): () => RoomLike {
  return () => new Room({ dynacast: true, adaptiveStream: false }) as unknown as RoomLike;
}

/**
 * Service registrations for the LiveKit room transport. Composes with
 * `createQuestVisionStreamRegistrations`' graph style: the room service takes
 * the `IWebRTCService` seam's place as the detections carrier, so the existing
 * `DetectionService` works unchanged on the new transport (RA-C7).
 */
export interface LiveKitRoomRegistrationOptions extends Omit<LiveKitRoomConfig, 'roomFactory'> {
  readonly roomFactory?: () => RoomLike;
  /** Consent policy (D6). See `AssistSessionConfig`. */
  readonly assist?: AssistSessionConfig;
  /** Stroke conversion flags. See `AssistStrokeConfig`. */
  readonly strokes?: AssistStrokeConfig;
  /** Server-authority training mirror. See `RemoteTrainingConfig`. */
  readonly training?: RemoteTrainingConfig;
}

export function createLiveKitRoomRegistrations(
  options: LiveKitRoomRegistrationOptions,
): ServiceRegistration[] {
  const { assist, strokes, training, ...roomOptions } = options;
  return [
    {
      token: ILiveKitRoomService,
      priority: 20,
      config: { roomFactory: createLiveKitRoomFactory(), ...roomOptions } satisfies LiveKitRoomConfig,
      useFactory: (ctx) => new LiveKitRoomService(ctx as ServiceActivationContext<LiveKitRoomConfig>),
    },
    // Priority order matters below: the roster must observe room events
    // before the session service asks it to (un)subscribe, and both must be
    // started before consent can have consequences.
    {
      token: IAssistantRosterService,
      priority: 22,
      dependencies: [ILiveKitRoomService],
      config: {} satisfies AssistantRosterConfig,
      useFactory: (ctx, room) =>
        new AssistantRosterService(
          ctx as ServiceActivationContext<AssistantRosterConfig>,
          room as ILiveKitRoomService,
        ),
    },
    {
      token: IAssistSessionService,
      priority: 24,
      dependencies: [ILiveKitRoomService, IAssistantRosterService],
      config: { ...assist } satisfies AssistSessionConfig,
      useFactory: (ctx, room, roster) =>
        new AssistSessionService(
          ctx as ServiceActivationContext<AssistSessionConfig>,
          room as ILiveKitRoomService,
          roster as IAssistantRosterService,
        ),
    },
    {
      token: IAssistStrokeService,
      priority: 26,
      dependencies: [ILiveKitRoomService],
      config: { ...strokes } satisfies AssistStrokeConfig,
      useFactory: (ctx, room) =>
        new AssistStrokeService(ctx as ServiceActivationContext<AssistStrokeConfig>, room as ILiveKitRoomService),
    },
    {
      token: IDetectionService,
      priority: 30,
      dependencies: [ILiveKitRoomService],
      config: {} satisfies DetectionConfig,
      useFactory: (ctx, room) =>
        new DetectionService(
          ctx as ServiceActivationContext<DetectionConfig>,
          // The room service is a structural superset of the IWebRTCService
          // seam DetectionService consumes (detectionMessage events).
          room as unknown as IWebRTCService,
        ),
    },
    {
      token: ITrainingStateService,
      priority: 32,
      dependencies: [ILiveKitRoomService, IDetectionService],
      config: { ...training } satisfies RemoteTrainingConfig,
      useFactory: (ctx, room, detections) =>
        new RemoteTrainingStateService(
          ctx as ServiceActivationContext<RemoteTrainingConfig>,
          room as ILiveKitRoomService,
          detections as IDetectionService,
        ),
    },
  ];
}
